import { useState, useEffect, useRef, RefObject } from "react";

export interface ContainerSize {
  width: number;
  height: number;
}

/**
 * Hook to track the dimensions of a container element using ResizeObserver.
 */
export function useContainerSize(): [
  RefObject<HTMLDivElement | null>,
  ContainerSize,
] {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState<ContainerSize>({ width: 0, height: 0 });

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    // Set initial size
    const rect = element.getBoundingClientRect();
    setSize({ width: rect.width, height: rect.height });

    // Create ResizeObserver to track size changes
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setSize({ width, height });
      }
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return [containerRef, size];
}

/**
 * Hook to get the viewport size, updated on resize.
 */
export function useViewportSize(): ContainerSize {
  const [size, setSize] = useState<ContainerSize>({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  });

  useEffect(() => {
    const handleResize = () => {
      setSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return size;
}
